import styled from "styled-components";

// ── Outer container ────────────────────────────────────────────────────────────
export const WizardContainer = styled.div`
  padding: 2rem;
`;

// ── Header ────────────────────────────────────────────────────────────────────
export const WizardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const WizardTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #2d3748;
`;

export const StepIndicator = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #718096;
  background: #edf2f7;
  padding: 0.5rem 1rem;
  border-radius: 20px;
`;

// ── Progress bar ──────────────────────────────────────────────────────────────
export const WizardProgress = styled.div`
  margin-bottom: 2rem;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #4299e1 0%, #667eea 100%);
  transition: width 0.3s ease;
`;

// ── Step content card ─────────────────────────────────────────────────────────
export const StepContent = styled.div`
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const StepTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
`;

export const StepDescription = styled.p`
  font-size: 0.875rem;
  color: #718096;
  margin: 0;
`;

// ── Form elements ─────────────────────────────────────────────────────────────
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: #fff;
  color: #2d3748;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: #fff;
  color: #2d3748;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }

  &:disabled {
    background: #edf2f7;
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  background: #fff;
  color: #2d3748;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
  }
`;

// ── Buttons ───────────────────────────────────────────────────────────────────
export const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

interface ButtonProps {
  variant?: "primary" | "secondary";
}

export const Button = styled.button<ButtonProps>`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.variant === "secondary"
      ? `
    background: #edf2f7;
    color: #4a5568;

    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  `
      : `
    background: linear-gradient(135deg, #4299e1 0%, #667eea 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ── Component mapping (step 2) ────────────────────────────────────────────────
export const ComponentMappingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 380px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

export const ComponentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0.75rem 1rem;
  background: #f7fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
`;

export const ComponentName = styled.div`
  flex: 1;
  font-size: 0.875rem;
  font-weight: 500;
  color: #2d3748;
`;

export const VersionSelect = styled(Select)`
  flex: 0 0 280px;
  width: auto;
`;

export const TypeBadge = styled.span<{ componentType: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${({ componentType }) =>
    componentType === "Container" ? "#dbeafe" : "#dcfce7"};
  color: ${({ componentType }) =>
    componentType === "Container" ? "#1d4ed8" : "#15803d"};
`;

// ── Review / Summary (step 3) ─────────────────────────────────────────────────
export const ReviewSection = styled.div`
  background: #f7fafc;
  padding: 1.5rem;
  border-radius: 6px;
  border: 1px solid #e2e8f0;

  h4 {
    margin: 0 0 12px 0;
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
  }
`;

export const ReviewItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
`;

export const ReviewLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  min-width: 140px;
`;

export const ReviewValue = styled.div`
  font-size: 0.875rem;
  color: #2d3748;
  flex: 1;
`;

export const ComponentsList = styled.div`
  h4 {
    margin: 0 0 12px 0;
    font-size: 1rem;
    font-weight: 600;
    color: #2d3748;
  }
`;

export const ComponentItem = styled.div`
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 0.875rem;
  color: #2d3748;

  &:last-child {
    margin-bottom: 0;
  }
`;

// ── Feedback ──────────────────────────────────────────────────────────────────
export const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2.5rem;
  color: #718096;
  font-size: 0.875rem;
`;

export const ErrorMessage = styled.div`
  padding: 1rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  background: #fff5f5;
  border: 1px solid #fc8181;
  color: #c53030;
`;

// ── Step 2 tag filter ─────────────────────────────────────────────────────────
export const TagFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

export const TagFilterInput = styled.input`
  flex: 1;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: #55b8b3;
    box-shadow: 0 0 0 2px rgba(85, 184, 179, 0.15);
  }
`;

export const TagFilterClear = styled.button`
  background: none;
  border: none;
  font-size: 14px;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;

  &:hover {
    color: #374151;
    background: #f3f4f6;
  }
`;
